//* Libraries imports
import { Modal, Button, Spinner, Alert, Badge, Card } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";

//* Icons imports
import { 
  FaFileAlt, 
  FaEye, 
  FaTimes, 
  FaInfoCircle, 
  FaListOl, 
  FaBookOpen,
  FaCheckCircle,
  FaExclamationTriangle
} from "react-icons/fa";
import { Icon } from "../../../../../../components/common/Icon";

//* Hooks imports
import { useGetEssayById } from "../../../../../../hooks/queries/essay/use-get-essay-template";

//* Styles imports
import "./essay-template-modal.scss";

interface EssayTemplateModalProps {
  show: boolean;
  onHide: () => void;
  processoId: number;
  essayId: number;
  essayTitle?: string;
}

export function EssayTemplateModal({
  show,
  onHide,
  processoId,
  essayId,
  essayTitle,
}: EssayTemplateModalProps) {
  const { data, isLoading, error } = useGetEssayById(processoId, essayId);

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { 
        duration: 0.3, 
        ease: [0.34, 1.56, 0.64, 1] as const,
        type: "spring" as const,
        stiffness: 300
      },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: { duration: 0.2, ease: "easeInOut" as const },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { 
        delay: 0.15, 
        duration: 0.4,
        ease: "easeOut" as const
      },
    },
  };

  const staggerVariants = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] as const }
    }
  };

  const renderLoadingState = () => (
    <motion.div 
      className="text-center py-5"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="loading-animation">
        <Spinner animation="border" variant="primary" size="sm" />
        <div className="loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
      <p className="mt-3 text-muted mb-0">Carregando informações da redação...</p>
      <small className="text-muted">Aguarde alguns instantes</small>
    </motion.div>
  );

  const renderErrorState = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Alert variant="danger" className="error-alert">        <div className="d-flex align-items-center">
          <Icon icon={FaExclamationTriangle} className="me-3 flex-shrink-0" size={20} />
          <div>
            <h6 className="mb-1">Erro ao carregar template</h6>
            <small>{data?.message || "Não foi possível carregar as informações da redação."}</small>
          </div>
        </div>
      </Alert>
    </motion.div>
  );

  const renderEmptyState = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Alert variant="warning" className="warning-alert">        <div className="d-flex align-items-center">
          <Icon icon={FaInfoCircle} className="me-3 flex-shrink-0" size={20} />
          <div>
            <h6 className="mb-1">Modelo não encontrado</h6>
            <small>Nenhum modelo de redação foi configurado para este item.</small>
          </div>
        </div>
      </Alert>
    </motion.div>
  );

  const renderContent = () => {
    if (isLoading) {
      return renderLoadingState();
    }

    if (error || !data?.success) {
      return renderErrorState();
    }

    const essayModel = data.data?.data?.essayModel;
    if (!essayModel) {
      return renderEmptyState();
    }

    return (
      <motion.div
        variants={contentVariants}
        initial="hidden"
        animate="visible"
        className="essay-template-content"
      >
        <motion.div variants={staggerVariants} initial="hidden" animate="visible">
          {/* Success Indicator */}          <motion.div variants={itemVariants} className="mb-4">
            <div className="success-indicator">
              <Icon icon={FaCheckCircle} className="text-success me-2" />
              <span className="text-success">Modelo carregado com sucesso</span>
            </div>
          </motion.div>

          {/* Header com informações básicas */}
          <motion.div variants={itemVariants}>
            <Card className="info-card mb-4">
              <Card.Body className="p-4">                <div className="card-header-custom">
                  <Icon icon={FaInfoCircle} className="icon-primary me-2" />
                  <h6 className="mb-0">Informações Gerais</h6>
                </div>
                <div className="row mt-3">
                  <div className="col-md-8">
                    <div className="info-item">
                      <label>Nome da Redação:</label>
                      <p className="value">{essayModel.name}</p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="info-item text-center">
                      <label>Número de Linhas:</label>                      <Badge bg="primary" className="lines-badge">
                        <Icon icon={FaListOl} className="me-1" />
                        {essayModel.numberLines} linhas
                      </Badge>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </motion.div>

          {/* Enunciado */}
          <motion.div variants={itemVariants}>
            <Card className="content-card mb-4">
              <Card.Body className="p-4">                <div className="card-header-custom">
                  <Icon icon={FaBookOpen} className="icon-primary me-2" />
                  <h6 className="mb-0">Enunciado</h6>
                </div>
                <div className="content-wrapper mt-3">
                  <div
                    className="content-display"
                    dangerouslySetInnerHTML={{ __html: essayModel.enunciado }}
                  />
                </div>
              </Card.Body>
            </Card>
          </motion.div>

          {/* Conteúdo Detalhado */}
          {essayModel.enunciadoContent && (
            <motion.div variants={itemVariants}>
              <Card className="content-card">
                <Card.Body className="p-4">                  <div className="card-header-custom">
                    <Icon icon={FaEye} className="icon-primary me-2" />
                    <h6 className="mb-0">Conteúdo Detalhado</h6>
                  </div>
                  <div className="content-wrapper mt-3">
                    <div
                      className="content-display"
                      dangerouslySetInnerHTML={{ __html: essayModel.enunciadoContent }}
                    />
                  </div>
                </Card.Body>
              </Card>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    );
  };

  return (
    <AnimatePresence mode="wait">
      {show && (        <Modal
          show={show}
          onHide={onHide}
          size="xl"
          centered
          className="essay-template-modal"
          backdrop={true}
          keyboard={true}
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <Modal.Header closeButton className="modal-header-custom">              <Modal.Title className="d-flex align-items-center">
                <div className="title-icon">
                  <Icon icon={FaFileAlt} />
                </div>
                <div className="title-content">
                  <h5 className="title-main">Modelo da Redação</h5>
                  {essayTitle && (
                    <small className="title-subtitle">{essayTitle}</small>
                  )}
                </div>
              </Modal.Title>
            </Modal.Header>

            <Modal.Body className="modal-body-custom">
              {renderContent()}
            </Modal.Body>

            <Modal.Footer className="modal-footer-custom">
              <Button
                variant="outline-secondary"                onClick={onHide}
                className="close-button"
              >
                <Icon icon={FaTimes} className="me-2" size={14} />
                Fechar
              </Button>
            </Modal.Footer>
          </motion.div>
        </Modal>
      )}
    </AnimatePresence>
  );
}
